import dayjs from "dayjs";
import { TFunction } from "i18next";
import { Goal, GoalTimestamp } from "../types/api";

export const getProficiencyLabel = (value: string, t: TFunction): string => {
  const levels = [
    { value: "00", label: t("no_proficiency") },
    { value: "A1", label: t("level_a1") },
    { value: "A2", label: t("level_a2") },
    { value: "B1", label: t("level_b1") },
    { value: "B2", label: t("level_b2") },
    { value: "C1", label: t("level_c1") },
    { value: "C2", label: t("level_c2") },
  ];

  const match = levels.find((level) => level.value === value);
  return match ? match.label : value; // fallback — повертає value, якщо не знайдено
};

export const getCurrencySymbol = (currency: string): string => {
  const symbols: Record<string, string> = {
    USD: "$", // US Dollar
    EUR: "€", // Euro
    UAH: "₴", // Ukrainian Hryvnia
    GBP: "£", // British Pound
    JPY: "¥", // Japanese Yen
    CNY: "¥", // Chinese Yuan
    PLN: "zł", // Polish Zloty
    CHF: "CHF", // Swiss Franc
    CAD: "$", // Canadian Dollar
    AUD: "$", // Australian Dollar
    SEK: "kr", // Swedish Krona
    NOK: "kr", // Norwegian Krone
    DKK: "kr", // Danish Krone
    CZK: "Kč", // Czech Koruna
    HUF: "Ft", // Hungarian Forint
    INR: "₹", // Indian Rupee
    KRW: "₩", // South Korean Won
    TRY: "₺", // Turkish Lira
    ILS: "₪", // Israeli Shekel
    AED: "د.إ", // UAE Dirham
    THB: "฿", // Thai Baht
    ZAR: "R", // South African Rand
    BRL: "R$", // Brazilian Real
    SGD: "$", // Singapore Dollar
    MYR: "RM", // Malaysian Ringgit
    IDR: "Rp", // Indonesian Rupiah
    PHP: "₱", // Philippine Peso
    VND: "₫", // Vietnamese Dong
    RON: "lei", // Romanian Leu
    BGN: "лв", // Bulgarian Lev
    HRK: "€", // Croatia (now uses Euro)
    ISK: "kr", // Icelandic Króna
    NZD: "$", // New Zealand Dollar
    MXN: "$", // Mexican Peso
    ARS: "$", // Argentine Peso
    CLP: "$", // Chilean Peso
    EGP: "£", // Egyptian Pound
    KZT: "₸", // Kazakhstani Tenge
    NGN: "₦", // Nigerian Naira
    PKR: "₨", // Pakistani Rupee
    IRR: "﷼", // Iranian Rial
    IQD: "ع.د", // Iraqi Dinar
    GEL: "₾", // Georgian Lari
    MKD: "ден", // Macedonian Denar
    BAM: "KM", // Bosnian Convertible Mark
    RSD: "дин", // Serbian Dinar
    BYN: "Br", // Belarusian Ruble
    AMD: "֏", // Armenian Dram
  };

  return symbols[currency.toUpperCase()] || currency.toUpperCase();
};

export function capitalizeFirstLetter(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export const LANGUAGE_CHOICES = [
  ["af", "Afrikaans"],
  ["sq", "Albanian"],
  ["am", "Amharic"],
  ["ar", "Arabic"],
  ["hy", "Armenian"],
  ["as", "Assamese"],
  ["ay", "Aymara"],
  ["az", "Azerbaijani"],
  ["bm", "Bambara"],
  ["eu", "Basque"],
  ["be", "Belarusian"],
  ["bn", "Bengali"],
  ["bho", "Bhojpuri"],
  ["bs", "Bosnian"],
  ["bg", "Bulgarian"],
  ["ca", "Catalan"],
  ["ceb", "Cebuano"],
  ["ny", "Chichewa"],
  ["zh-CN", "Chinese (simplified)"],
  ["zh-TW", "Chinese (traditional)"],
  ["zh", "Chinese"],
  ["co", "Corsican"],
  ["hr", "Croatian"],
  ["cs", "Czech"],
  ["da", "Danish"],
  ["dv", "Dhivehi"],
  ["doi", "Dogri"],
  ["nl", "Dutch"],
  ["en", "English"],
  ["eo", "Esperanto"],
  ["et", "Estonian"],
  ["ee", "Ewe"],
  ["tl", "Filipino"],
  ["fi", "Finnish"],
  ["fr", "French"],
  ["fy", "Frisian"],
  ["gl", "Galician"],
  ["ka", "Georgian"],
  ["de", "German"],
  ["el", "Greek"],
  ["gn", "Guarani"],
  ["gu", "Gujarati"],
  ["ht", "Haitian creole"],
  ["ha", "Hausa"],
  ["haw", "Hawaiian"],
  ["iw", "Hebrew"],
  ["hi", "Hindi"],
  ["hmn", "Hmong"],
  ["hu", "Hungarian"],
  ["is", "Icelandic"],
  ["ig", "Igbo"],
  ["ilo", "Ilocano"],
  ["id", "Indonesian"],
  ["ga", "Irish"],
  ["it", "Italian"],
  ["ja", "Japanese"],
  ["jw", "Javanese"],
  ["kn", "Kannada"],
  ["kk", "Kazakh"],
  ["km", "Khmer"],
  ["rw", "Kinyarwanda"],
  ["gom", "Konkani"],
  ["ko", "Korean"],
  ["kri", "Krio"],
  ["ku", "Kurdish (kurmanji)"],
  ["ckb", "Kurdish (sorani)"],
  ["ky", "Kyrgyz"],
  ["lo", "Lao"],
  ["la", "Latin"],
  ["lv", "Latvian"],
  ["ln", "Lingala"],
  ["lt", "Lithuanian"],
  ["lg", "Luganda"],
  ["lb", "Luxembourgish"],
  ["mk", "Macedonian"],
  ["mai", "Maithili"],
  ["mg", "Malagasy"],
  ["ms", "Malay"],
  ["ml", "Malayalam"],
  ["mt", "Maltese"],
  ["mi", "Maori"],
  ["mr", "Marathi"],
  ["mni-Mtei", "Meiteilon (manipuri)"],
  ["lus", "Mizo"],
  ["mn", "Mongolian"],
  ["my", "Myanmar"],
  ["ne", "Nepali"],
  ["no", "Norwegian"],
  ["or", "Odia (oriya)"],
  ["om", "Oromo"],
  ["ps", "Pashto"],
  ["fa", "Persian"],
  ["pl", "Polish"],
  ["pt", "Portuguese"],
  ["pa", "Punjabi"],
  ["qu", "Quechua"],
  ["ro", "Romanian"],
  ["ru", "Russian"],
  ["sm", "Samoan"],
  ["sa", "Sanskrit"],
  ["gd", "Scots gaelic"],
  ["nso", "Sepedi"],
  ["sr", "Serbian"],
  ["st", "Sesotho"],
  ["sn", "Shona"],
  ["sd", "Sindhi"],
  ["si", "Sinhala"],
  ["sk", "Slovak"],
  ["sl", "Slovenian"],
  ["so", "Somali"],
  ["es", "Spanish"],
  ["su", "Sundanese"],
  ["sw", "Swahili"],
  ["sv", "Swedish"],
  ["tg", "Tajik"],
  ["ta", "Tamil"],
  ["tt", "Tatar"],
  ["te", "Telugu"],
  ["th", "Thai"],
  ["ti", "Tigrinya"],
  ["ts", "Tsonga"],
  ["tr", "Turkish"],
  ["tk", "Turkmen"],
  ["ak", "Twi"],
  ["uk", "Ukrainian"],
  ["ur", "Urdu"],
  ["ug", "Uyghur"],
  ["uz", "Uzbek"],
  ["vi", "Vietnamese"],
  ["cy", "Welsh"],
  ["xh", "Xhosa"],
  ["yi", "Yiddish"],
  ["yo", "Yoruba"],
  ["zu", "Zulu"],
];

export function getLanguageName(code: string): string {
  const found = LANGUAGE_CHOICES.find(([key]) => key === code);
  return found ? found[1] : code;
}

export function getLanguageCode(name: string): string {
  const found = LANGUAGE_CHOICES.find(
    ([, label]) => label.toLowerCase() === name.toLowerCase()
  );
  return found ? found[0] : name;
}

export const formatPlatform = (platform: string): string => {
  switch (platform.toLowerCase()) {
    case "edx":
      return "edX";
    case "linkedin":
      return "LinkedIn";
    case "udemy":
      return "Udemy";
    case "prometheus":
      return "Prometheus";
    case "diya":
      return "Дія.Освіта";
    case "coursera":
      return "Coursera";
    default:
      return platform.charAt(0).toUpperCase() + platform.slice(1);
  }
};

export const normalizeLocale = (lang: string): string => {
  const localeMap: Record<string, string> = {
    en: "en-GB", // або en-US
    uk: "uk-UA",
    pl: "pl-PL",
    de: "de-DE",
    fr: "fr-FR",
    // додай інші за потреби
  };

  return localeMap[lang] || "en-GB"; // дефолт
};

export const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: "smooth",
  });
};
